/**
 * GuideArticlePage — renders one article inside the Field Guide.
 *
 * URL shapes:
 *   /guide/<section>/<article>           (EN default)
 *   /ru/guide/<section>/<article>        (RU)
 *
 * Falls back to a skeleton while loading and a 404 when the slug pair is
 * unknown or no content module exists for the language.
 */

import { useEffect, useMemo, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDocumentLang } from '@/hooks/useDocumentLang';
import { useJsonLd, useSeoMeta } from '@/hooks/useSeoMeta';
import {
  ARTICLES,
  ArticleBody,
  buildGuideUrl,
  buildFaqsForJsonLd,
  buildGuideArticleJsonLd,
  buildRegistryForArticle,
  getSectionDef,
  isArticleSlug,
  isSectionSlug,
  useGuideArticle,
  useGuideHreflang,
  type ArticleSlug,
  type LangCode,
  type SectionSlug,
} from '@/features/guide';

interface GuideArticlePageProps {
  lang: LangCode;
}

export default function GuideArticlePage({ lang }: GuideArticlePageProps) {
  useDocumentLang();
  const { t, i18n } = useTranslation();
  const { section: sectionParam, article: articleParam } = useParams<{
    section?: string;
    article?: string;
  }>();

  useEffect(() => {
    if (i18n.language !== lang) {
      i18n.changeLanguage(lang).catch(() => {});
    }
  }, [lang, i18n]);

  const sectionSlug = useMemo<SectionSlug | null>(
    () => (isSectionSlug(sectionParam) ? (sectionParam as SectionSlug) : null),
    [sectionParam],
  );
  const articleSlug = useMemo<ArticleSlug | null>(
    () => (isArticleSlug(articleParam) ? (articleParam as ArticleSlug) : null),
    [articleParam],
  );

  const { article, loading } = useGuideArticle(lang, sectionSlug, articleSlug);

  const guideLabel = t('guide.label', { defaultValue: 'Field Guide' });
  const sectionLabel = sectionSlug
    ? t(`guide.section.${getSectionDef(sectionSlug).i18nKey}.label`, {
        defaultValue: getSectionDef(sectionSlug).i18nKey,
      })
    : '';
  const articleTitle = article?.meta.title
    ?? (articleSlug ? t(`guide.article.${articleSlug}.title`, { defaultValue: articleSlug }) : '');
  const articleDescription = article?.meta.description
    ?? (articleSlug ? t(`guide.article.${articleSlug}.summary`, { defaultValue: '' }) : '');

  // Sticky progress bar
  const progressRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    let raf = 0;
    const update = () => {
      raf = 0;
      const doc = document.documentElement;
      const total = doc.scrollHeight - doc.clientHeight;
      const next = total > 0 ? Math.min(1, Math.max(0, doc.scrollTop / total)) : 0;
      if (progressRef.current) {
        progressRef.current.style.transform = `scaleX(${next})`;
      }
    };
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(update);
    };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  // OG image: generated per article by scripts/gen-og-guide.mjs into
  //   /public/og/guide/<lang>/<section>--<slug>.png
  // The article module can override via meta.ogImage if it ever needs a
  // custom hero; default to the generated PNG so social previews always
  // carry the article title + section without a default fallback.
  const generatedOgImage = sectionSlug && articleSlug
    ? `https://lodestar.place/og/guide/${lang}/${sectionSlug}--${articleSlug}.png`
    : undefined;

  useSeoMeta({
    title: articleTitle ? `${articleTitle} — ${guideLabel}` : guideLabel,
    description: articleDescription || guideLabel,
    canonical:
      sectionSlug && articleSlug
        ? buildGuideUrl({ lang, section: sectionSlug, article: articleSlug, origin: 'https://lodestar.place' })
        : undefined,
    locale: lang === 'ru' ? 'ru_RU' : 'en_US',
    ogType: 'article',
    ogImage: article?.meta.ogImage ?? generatedOgImage,
  });

  useGuideHreflang({
    section: sectionSlug ?? undefined,
    article: articleSlug ?? undefined,
  });

  const resolvedFaqs = article
    ? buildFaqsForJsonLd(article.faqs, buildRegistryForArticle(article))
    : [];

  useJsonLd(
    article && sectionSlug && articleSlug
      ? buildGuideArticleJsonLd({
          lang,
          section: sectionSlug,
          article: articleSlug,
          guideLabel,
          sectionLabel,
          headline: articleTitle,
          description: articleDescription,
          datePublished: article.meta.updated,
          dateModified: article.meta.updated,
          author: article.meta.author,
          faqs: resolvedFaqs.length > 0 ? resolvedFaqs : undefined,
        })
      : null,
    'guide-article',
  );

  if (!sectionSlug || !articleSlug) return <NotFound lang={lang} />;
  if (loading) return <ArticleSkeleton />;
  if (!article) return <NotFound lang={lang} />;

  const related = article.related
    .map((r) => ARTICLES.find((a) => a.slug === r.article))
    .filter((a): a is NonNullable<typeof a> => !!a);

  return (
    <>
      <div
        aria-hidden="true"
        className="pointer-events-none fixed inset-x-0 top-0 z-[1200] h-[2px] sm:h-[4px]"
      >
        <div
          ref={progressRef}
          className="h-full origin-left bg-terra-600 transition-transform duration-75 motion-reduce:transition-none"
          style={{ transform: 'scaleX(0)' }}
        />
      </div>

      <article className="mx-auto w-full max-w-3xl px-4 py-8 sm:py-12">
        <nav
          aria-label="breadcrumb"
          className="mb-5 font-mono text-[10px] uppercase tracking-wider text-ink-400"
        >
          <Link to={buildGuideUrl({ lang })} className="hover:text-ink-700">
            {guideLabel}
          </Link>
          <span aria-hidden="true" className="mx-1.5">/</span>
          <Link to={buildGuideUrl({ lang, section: sectionSlug })} className="hover:text-ink-700">
            {sectionLabel}
          </Link>
          <span aria-hidden="true" className="mx-1.5">/</span>
          <span className="text-ink-700">{articleTitle}</span>
        </nav>

        <header className="mb-8 sm:mb-10">
          <p className="font-mono text-[10px] uppercase tracking-wider text-ink-500">
            {sectionLabel}
          </p>
          <h1 className="mt-2 font-display text-[clamp(28px,6vw,40px)] font-medium italic tracking-tight text-ink-900">
            {articleTitle}
          </h1>
        </header>

        <ArticleBody article={article} lang={lang} />

        {related.length > 0 && (
          <section aria-labelledby="related-title" className="mt-14">
            <h2
              id="related-title"
              className="font-display text-lg font-medium italic tracking-tight text-ink-900 sm:text-xl"
            >
              {t('guide.relatedArticles', { defaultValue: 'Related' })}
            </h2>
            <ul className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {related.map((r) => (
                <li key={r.slug}>
                  <Link
                    to={buildGuideUrl({ lang, section: r.section, article: r.slug })}
                    className="flex flex-col rounded-md border border-ink-100 bg-paper-2/60 px-4 py-3 transition-colors hover:bg-paper-2"
                  >
                    <span className="font-mono text-[10px] uppercase tracking-wider text-ink-500">
                      {t(`guide.section.${getSectionDef(r.section).i18nKey}.label`, {
                        defaultValue: r.section,
                      })}
                    </span>
                    <span className="mt-1 font-display text-base font-medium italic text-ink-900">
                      {t(`guide.article.${r.slug}.title`, { defaultValue: r.slug })}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        )}
      </article>
    </>
  );
}

function ArticleSkeleton() {
  return (
    <div aria-hidden="true" className="mx-auto w-full max-w-3xl animate-pulse px-4 py-12">
      <div className="mb-4 h-3 w-32 rounded bg-ink-100" />
      <div className="mb-3 h-10 w-3/4 rounded bg-ink-100" />
      <div className="mb-8 h-4 w-2/3 rounded bg-ink-100" />
      <div className="space-y-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="h-4 w-full rounded bg-ink-100" />
        ))}
      </div>
    </div>
  );
}

function NotFound({ lang }: { lang: LangCode }) {
  const { t } = useTranslation();
  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col items-center px-4 py-20 text-center">
      <h1 className="font-display text-3xl font-medium italic tracking-tight text-ink-900">
        {t('guide.notFound.title', { defaultValue: 'Page not found' })}
      </h1>
      <p className="mt-3 text-sm text-ink-500">
        {t('guide.notFound.subtitle', { defaultValue: 'This page does not exist in the Field Guide.' })}
      </p>
      <Link
        to={buildGuideUrl({ lang })}
        className="mt-6 inline-flex h-11 items-center rounded-md bg-terra-600 px-5 text-sm font-medium text-paper hover:bg-terra-700"
      >
        {t('guide.notFound.cta', { defaultValue: 'Back to Field Guide' })}
      </Link>
    </div>
  );
}
